<template>
  <v-dialog v-model="isLogoutTimerDialogVisible" width="300px" @input="logoutTimerDialogOnClose">
    <v-card class="pa-4">

      <div>{{$t('appIdleHandler.labels.sessionWillBeClosed')}}</div>

      <div class="d-flex justify-center my-2">
        <v-progress-circular :rotate="-90" :value="percentsLeftToLogout" :size="40" color="accent">
          {{secondsLeftToLogout}}
        </v-progress-circular>
      </div>

      <div class="d-flex justify-center">
        <v-btn color="primary" text @click="logout"> {{$t('buttons.confirm')}} </v-btn>
        <v-btn text @click="closeLogoutTimerDialog"> {{$t('buttons.cancel')}} </v-btn>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import uiMixin from '@/mixins/ui-mixin';
import { USER_UI_EVENTS_TO_LISTEN_TO_PROCESS_IDLE } from '@/models/defaults';
import { actions as _act } from '@/models/store.actions';
import { logHelper } from '@/helpers';
import { AppError } from '@/models/log.models';
import { logger } from '@/helpers/log.service';
import { isNativeApp } from '@/helpers/platform-helper';

const USER_ACTIVITY_PROCESSING_DELAY_TIME = 2000;
const USER_ACTIVITY_IDLE_TIME = 10 * 60 * 1000;

export default {
  name: 'AppIdleHandler',
  mixins: [uiMixin],
  inject: ['isMobile'],
  data() {
    return {
      userActivityIdleTimeout: null,
      userActivityProcessingTimeout: null,
      logoutCounterInterval: null,
      percentsLeftToLogout: 100,
      secondsLeftToLogout: 0,
      isLogoutTimerDialogVisible: false,
      worker: null
    };
  },
  computed: {
    isLogined() {
      return this.$store.getters[_act.AUTH_GET_ISLOGINEDGET];
    },
    userSettings() {
      return this.$store.getters[_act.USETT_GET_USERSETTINGS];
    },
    userEmail() {
      return this.$store.getters[_act.AUTH_GET_EMAILUSERGET];
    },
    isWorkerSupported() {
      return !!window.Worker;
    },
    isIdleHandlerEnabled() {
      return !this.isMobile && !isNativeApp();
    }
  },
  mounted() {
    this.initIdleHandler();
  },
  methods: {
    initIdleHandler() {
      if (this.isIdleHandlerEnabled) {
        this.subscribeOnAppEvents();
        this.subscribeOnStorageEvents();
        this.tryToBeginProcessingUserActivityIdle();
      }
    },
    subscribeOnAppEvents() {
      logger.debug('[AppIdleHandler:subscribeOnAppEvents]');
      this.$eventBus.$on(
        'user-logged',
        this.tryToBeginProcessingUserActivityIdle
      );
      this.$eventBus.$on(
        'user-logged-out',
        this.stopProcessingUserActivityIdle
      );
      this.$eventBus.$on(
        'user-settings-changed',
        this.processChangingUserSettings
      );
    },
    subscribeOnStorageEvents() {
      logger.debug('[AppIdleHandler:subscribeOnStorageEvents]');
      window.addEventListener('storage', (e) => {
        if (e && e.newValue && e.newValue === this.userEmail) {
          logger.debug(
            `[AppIdleHandler:storageListener] key=${e.key} email=${e.newValue}`
          );
          if (e.key === 'userUiActivity') {
            this.processUserUiActivity(e);
          } else if (e.key === 'userSettingsChanged') {
            this.processChangingUserSettings(e);
          }
        }
      });
    },
    async tryToBeginProcessingUserActivityIdle() {
      logger.debug('[AppIdleHandler:tryToBeginProcessingUserActivityIdle]');
      if (
        this.isLogined &&
        !this.userActivityIdleTimeout &&
        (await this.getUserAutoLogoutSettingValue())
      ) {
        this.beginProcessingUserActivityIdle();
      }
    },
    async beginProcessingUserActivityIdle() {
      logger.debug('[AppIdleHandler:beginProcessingUserActivityIdle]');
      this.beginListeningUiEvents();
      this.beginCountingIdleTime();
    },
    stopProcessingUserActivityIdle() {
      logger.debug('[AppIdleHandler:stopProcessingUserActivityIdle]');
      this.stopListeningUiEvents();
      this.clearUserActivityProcessingTimeout();
      this.stopCountingIdleTime();
      this.stopLogoutCountdown();
      this.isLogoutTimerDialogVisible = false;
    },
    async processChangingUserSettings(e) {
      logger.debug('[AppIdleHandler:processChangingUserSettings] event: ', e);
      if (this.isLogined) {
        let isStorageEvent = e instanceof StorageEvent;
        let autoLogoutSettingValue = await this.getUserAutoLogoutSettingValue(
          isStorageEvent
        );
        if (!autoLogoutSettingValue) {
          this.stopProcessingUserActivityIdle();
        } else if (!this.userActivityIdleTimeout) {
          this.beginProcessingUserActivityIdle();
        }
        if (!isStorageEvent) {
          localStorage.setItem('userSettingsChanged', this.userEmail);
          localStorage.removeItem('userSettingsChanged');
        }
      }
    },

    beginListeningUiEvents() {
      logger.debug('[AppIdleHandler:beginListeningUiEvents]');
      USER_UI_EVENTS_TO_LISTEN_TO_PROCESS_IDLE.forEach((e) =>
        window.addEventListener(e, this.processUserUiActivity, true)
      );
      this.$eventBus.$on('frame-ui-event', this.processUserUiActivity);
    },
    stopListeningUiEvents() {
      logger.debug('[AppIdleHandler:stopListeningUiEvents]');
      USER_UI_EVENTS_TO_LISTEN_TO_PROCESS_IDLE.forEach((e) =>
        window.removeEventListener(e, this.processUserUiActivity, true)
      );
      this.$eventBus.$off('frame-ui-event', this.processUserUiActivity);
    },

    clearUserActivityProcessingTimeout() {
      clearTimeout(this.userActivityProcessingTimeout);
      this.userActivityProcessingTimeout = null;
    },
    processUserUiActivity(e) {
      if (!this.userActivityProcessingTimeout) {
        this.userActivityProcessingTimeout = setTimeout(() => {
          logger.debug('[AppIdleHandler:processUserUiActivity]', e);
          if (!(e instanceof StorageEvent)) {
            localStorage.setItem('userUiActivity', this.userEmail);
            localStorage.removeItem('userUiActivity');
          }
          if (this.userActivityProcessingTimeout) {
            this.stopCountingIdleTime();
            this.beginCountingIdleTime();
            this.clearUserActivityProcessingTimeout();
          }
        }, USER_ACTIVITY_PROCESSING_DELAY_TIME);
      }
    },

    beginCountingIdleTime() {
      this.userActivityIdleTimeout = setTimeout(
        this.beginCountingTimeToLogout,
        USER_ACTIVITY_IDLE_TIME
      );
    },
    stopCountingIdleTime() {
      clearTimeout(this.userActivityIdleTimeout);
      this.userActivityIdleTimeout = null;
    },

    beginCountingTimeToLogout() {
      if (this.userActivityIdleTimeout) {
        this.stopProcessingUserActivityIdle();
        this.showLogoutTimerDialog();
      }
    },
    showLogoutTimerDialog() {
      this.secondsLeftToLogout = 60;
      this.percentsLeftToLogout = 100;
      this.isLogoutTimerDialogVisible = true;
      let f = () => {
        this.secondsLeftToLogout--;
        this.percentsLeftToLogout -= 1.67;
        if (this.secondsLeftToLogout === 0) {
          this.stopLogoutCountdown();
          this.logout();
        }
      };
      this.startLogoutCountdown(f.bind(this), 1000);
    },
    async startLogoutCountdown(f, ms) {
      logger.debug('[AppIdleHandler:startLogoutCountdown] ms', ms);
      await this.initWorker();
      if (this.worker) {
        this.worker.onmessage = (e) => {
          let [, interval] = e.data;
          logger.debug('[AppIdleHandler:WorkerMessageListener]', interval);
          f();
        };
        this.worker.postMessage(['startInterval', ms]);
      } else {
        this.logoutCounterInterval = setInterval(() => {
          if (this.logoutCounterInterval) {
            f();
          }
        }, ms);
      }
    },
    stopLogoutCountdown() {
      logger.debug('[AppIdleHandler:stopLogoutCountdown]');
      if (this.worker) {
        this.worker.postMessage(['stopInterval']);
      } else {
        clearInterval(this.logoutCounterInterval);
        this.logoutCounterInterval = null;
      }
    },
    logoutTimerDialogOnClose(val) {
      this.stopLogoutCountdown();
      this.isLogoutTimerDialogVisible = val;
      this.beginProcessingUserActivityIdle();
    },
    closeLogoutTimerDialog() {
      this.logoutTimerDialogOnClose(false);
    },

    async logout() {
      this.isLogoutTimerDialogVisible = false;
      this.stopLogoutCountdown();
      let loader = this.showLoading();
      try {
        await this.$store.dispatch(_act.AUTH_ACT_LOGOUT);
      } catch (err) {
        if (err instanceof AppError === false) {
          logHelper.error(err);
        }
        this.beginProcessingUserActivityIdle();
      } finally {
        loader.hide();
      }
    },

    async getUserSettings(forceReload) {
      if (!this.userSettings || forceReload) {
        try {
          await this.$store.dispatch(_act.USETT_ACT_USERSETTINGS);
        } catch (_) {
          // ignored
        }
      }
      return this.userSettings;
    },
    async getUserAutoLogoutSettingValue(forceReload) {
      let autoLogoutSettingValue = false;
      let settings = await this.getUserSettings(forceReload);
      if (settings && settings.length) {
        let autoLogoutSetting = settings.find(
          (s) => s.settingCode === 'autoLogout'
        );
        if (autoLogoutSetting) {
          autoLogoutSettingValue = /true/i.test(
            autoLogoutSetting.value || autoLogoutSetting.defaultValue
          );
        }
      }
      return autoLogoutSettingValue;
    },

    async initWorker() {
      logger.debug(
        '[AppIdleHandler:initWorker] worker supported: ',
        this.isWorkerSupported
      );
      if (this.isWorkerSupported && !this.worker) {
        const TimersWorker = (
          await import('worker-loader!../../workers/timers-worker.js')
        ).default;
        this.worker = new TimersWorker();
        logger.debug('[AppIdleHandler:initWorker] worker initialized');
      }
    }
  }
};
</script>
